import React from 'react';
import { Award, Heart, Users, Leaf } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="pt-20">
      <SEO 
        title="Über uns - Ali Baba Kebab & Vegie Sarnen | Unsere Geschichte"
        description="Erfahren Sie mehr über Ali Baba Kebab & Vegie in Sarnen. Seit Jahren servieren wir frische, authentische Speisen mit Leidenschaft. Qualität, Frische und herzlicher Service."
        keywords="Ali Baba Sarnen, Restaurant Geschichte, Kebab Restaurant, Vegetarisches Restaurant Sarnen"
        path="/about"
      />
      
      {/* Hero Section */}
      <section className="section bg-[#1C1917] text-center">
        <div className="container mx-auto">
          <h1 className="text-6xl md:text-7xl text-white mb-6">ÜBER UNS</h1>
          <p className="text-xl text-[#F7E39A] max-w-3xl mx-auto">
            Ihre erste Adresse für authentische Kebab-Spezialitäten und vegetarische Köstlichkeiten in Sarnen
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section bg-[#0F0E0D]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl text-[#F7E39A] mb-6">Unsere Geschichte</h2>
              <p className="text-[#E3E3E3] text-lg mb-4 leading-relaxed">
                Seit vielen Jahren sind wir stolz darauf, den Menschen in Sarnen und Umgebung frische, authentische Speisen zu servieren. Was als kleine Idee begann, ist heute ein beliebter Treffpunkt für alle, die echten Geschmack schätzen.
              </p>
              <p className="text-[#E3E3E3] text-lg mb-4 leading-relaxed">
                Bei Ali Baba Kebab & Vegie verbinden wir traditionelle Rezepte mit modernen Ansprüchen. Ob knuspriger Kebab, saftige Grillspezialitäten oder kreative vegetarische Gerichte – bei uns findet jeder etwas nach seinem Geschmack.
              </p>
              <p className="text-[#E3E3E3] text-lg leading-relaxed">
                Unser Team bereitet täglich mit viel Liebe und Sorgfalt jedes Gericht zu. Frische Zutaten, sorgfältige Zubereitung und ein herzlicher Service sind für uns selbstverständlich.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_74889022-3a14-4f9f-9a7b-69c3c5635b4b/artifacts/19mnedgm_Tabela.webp" 
                alt="Restaurant Menü" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-[#1C1917]">
        <div className="container mx-auto">
          <h2 className="text-5xl text-center text-white mb-16">Unsere Werte</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-[#2A2725] w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="text-[#F7E39A]" size={48} />
              </div>
              <h3 className="text-2xl text-[#F7E39A] mb-3">Qualität</h3>
              <p className="text-[#E3E3E3]">
                Nur die besten Zutaten kommen in unsere Küche. Qualität hat bei uns oberste Priorität.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#2A2725] w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="text-[#F7E39A]" size={48} />
              </div>
              <h3 className="text-2xl text-[#F7E39A] mb-3">Leidenschaft</h3>
              <p className="text-[#E3E3E3]">
                Jedes Gericht wird mit Herz und Leidenschaft zubereitet – das schmeckt man.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#2A2725] w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="text-[#F7E39A]" size={48} />
              </div>
              <h3 className="text-2xl text-[#F7E39A] mb-3">Gemeinschaft</h3>
              <p className="text-[#E3E3E3]">
                Wir sind mehr als ein Restaurant – wir sind Teil der Sarner Gemeinschaft.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#2A2725] w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <Leaf className="text-[#A7C957]" size={48} />
              </div>
              <h3 className="text-2xl text-[#A7C957] mb-3">Frische</h3>
              <p className="text-[#E3E3E3]">
                Täglich frisch zubereitet – keine Kompromisse, nur das Beste für Sie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section bg-[#0F0E0D]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl text-white mb-8">Unsere Philosophie</h2>
          <p className="text-[#E3E3E3] text-lg mb-6 leading-relaxed">
            Bei Ali Baba glauben wir daran, dass gutes Essen Menschen zusammenbringt. Deshalb legen wir grossen Wert darauf, dass jeder Gast sich bei uns willkommen fühlt – egal ob Fleischliebhaber, Vegetarier oder Veganer.
          </p>
          <p className="text-[#E3E3E3] text-lg mb-6 leading-relaxed">
            Unser breites Angebot an vegetarischen und veganen Optionen zeigt: Genuss kennt keine Grenzen. Von klassischen Kebabs über saftige Grillgerichte bis hin zu kreativen Veggie-Spezialitäten – bei uns kommt jeder auf seine Kosten.
          </p>
          <div className="bg-[#F7E39A] text-[#1C1917] p-8 rounded-lg mt-8">
            <p className="text-2xl font-semibold italic">
              "Aus Sarnen für Sarnen – mit Liebe serviert."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
